import { fetchAuthSession } from 'aws-amplify/auth';
import { Minus, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { waitlistApi } from '../../services/waitlistApi';

import { waitlistErrorText } from './waitlistError';

interface WaitlistJoinModalProps {
  sessionId: string;
  activityTitle: string;
  sessionLabel: string; // already-formatted "2026-05-10 10:00 AM"
  childRequired: boolean;
  adultRequired: boolean;
  initialChildren?: number;
  initialAdults?: number;
  currentWaitlistCount: number;
  onClose: () => void;
  onJoined: () => void; // caller refetches the session waitlist status
}

/**
 * Lightweight modal for joining a session waitlist.
 *
 * Pulls a fresh Cognito access token at submit time so callers don't have to
 * pipe one in. Shows a clear "fairness disclaimer" so users know that joining
 * does not guarantee a spot (PRD §1.3).
 */
export default function WaitlistJoinModal({
  sessionId,
  activityTitle,
  sessionLabel,
  childRequired,
  adultRequired,
  initialChildren = 1,
  initialAdults = 1,
  currentWaitlistCount,
  onClose,
  onJoined,
}: WaitlistJoinModalProps) {
  const { t, i18n } = useTranslation('waitlist');

  const [children, setChildren] = useState(childRequired ? initialChildren : 0);
  const [adults, setAdults] = useState(adultRequired ? initialAdults : 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const minChildren = childRequired ? 1 : 0;
  const minAdults = adultRequired ? 1 : 0;
  const total = children + adults;

  const handleSubmit = async () => {
    setError(null);
    if (total < 1) {
      setError(t('join.needOne'));
      return;
    }
    setSubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        setError(t('signInFirst'));
        setSubmitting(false);
        return;
      }
      await waitlistApi.join(token, {
        sessionId,
        childrenCount: children,
        adultsCount: adults,
      });
      onJoined();
    } catch (err) {
      setError(waitlistErrorText(err, i18n, 'join.failed'));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-lg font-semibold text-charcoal">{t('join.title')}</h2>
          <button onClick={onClose} className="text-charcoal/60 hover:text-charcoal" type="button">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5 text-sm text-charcoal">
          <div>
            <div className="font-medium">{activityTitle}</div>
            <div className="text-charcoal/70">{sessionLabel}</div>
          </div>

          <div className="space-y-3">
            <Counter
              label={t('join.children')}
              value={children}
              min={minChildren}
              max={20}
              onChange={setChildren}
              disabled={submitting}
            />
            <Counter
              label={t('join.adults')}
              value={adults}
              min={minAdults}
              max={20}
              onChange={setAdults}
              disabled={submitting}
            />
          </div>

          <div className="rounded-lg bg-warm-white px-4 py-3 text-xs leading-relaxed text-charcoal/80">
            {t('join.notice')}
            <div className="mt-1.5 text-charcoal/60">{t('join.noGuarantee')}</div>
            <div className="mt-1.5 text-charcoal/60">
              {t('join.count', { count: currentWaitlistCount })}
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4">
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-medium text-charcoal hover:bg-soft-gray/50 disabled:opacity-50"
            type="button"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || total < 1}
            className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            type="button"
          >
            {submitting ? t('join.joining') : t('join.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Counter({
  label,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-charcoal">{label}</span>
      <div className="flex items-center gap-3">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={disabled || value <= min}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-soft-gray text-charcoal hover:bg-soft-gray/50 disabled:opacity-40"
          type="button"
        >
          <Minus size={14} />
        </button>
        <span className="min-w-[1.5rem] text-center font-medium text-charcoal">{value}</span>
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={disabled || value >= max}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-soft-gray text-charcoal hover:bg-soft-gray/50 disabled:opacity-40"
          type="button"
        >
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}
